import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Send, Loader2, MessageSquare, Users } from "lucide-react";

/**
 * Modal universal para envio de WhatsApp.
 *
 * Props:
 *  - open (bool)
 *  - onClose ()
 *  - clients: [{id, full_name, whatsapp?}] — sempre passar array (1 ou N)
 *  - contract: { id, contract_type, code, ... } opcional — anexa ao envio quando 1 cliente
 *  - defaultCategory: string (destaca templates da categoria)
 *  - onSent: (result) => void
 */
export default function SendWhatsAppModal({ open, onClose, clients = [], contract = null, defaultCategory = "", onSent }) {
  const [templates, setTemplates] = useState([]);
  const [chosenCode, setChosenCode] = useState("");
  const [content, setContent] = useState("");
  const [preview, setPreview] = useState("");
  const [sending, setSending] = useState(false);
  const [config, setConfig] = useState(null);
  const [category, setCategory] = useState(defaultCategory);

  const isBulk = clients.length > 1;

  useEffect(() => {
    if (!open) return;
    setContent("");
    setPreview("");
    setChosenCode("");
    api.get("/whatsapp/templates", { params: { active: true } }).then(r => setTemplates(r.data)).catch(()=>{});
    api.get("/whatsapp/config/default").then(r => setConfig(r.data)).catch(()=>{});
  }, [open]);

  const sortedTemplates = useMemo(() => {
    if (!defaultCategory) return templates;
    return [...templates].sort((a,b) => {
      if (a.category === defaultCategory && b.category !== defaultCategory) return -1;
      if (b.category === defaultCategory && a.category !== defaultCategory) return 1;
      return 0;
    });
  }, [templates, defaultCategory]);

  const applyTemplate = async (code) => {
    setChosenCode(code);
    const tpl = templates.find(t => t.code === code);
    if (!tpl) { setContent(""); setPreview(""); return; }
    setContent(tpl.content);
    setCategory(tpl.category);
    // Preview with first client
    const c = clients[0] || {};
    try {
      const { data } = await api.post("/whatsapp/render-template", {
        content: tpl.content,
        variables: {
          nome: c.full_name || c.name || "",
          contrato: contract?.code || contract?.id?.slice(0,8) || "",
          valor: contract?.balance ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(contract.balance) : "",
          juros: contract?.interest_amount ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(contract.interest_amount) : "",
          vencimento: (contract?.due_date || "").slice(0,10),
        },
      });
      setPreview(data.rendered);
    } catch (e) { /* silent */ }
  };

  const doSend = async () => {
    if (!content || content.trim().length < 3) {
      toast.error("Escreva o conteúdo ou selecione um template");
      return;
    }
    setSending(true);
    try {
      if (isBulk) {
        const { data } = await api.post("/whatsapp/send-bulk", {
          client_ids: clients.map(c => c.id),
          template_code: chosenCode || null,
          content: chosenCode ? null : content,
          category: category || null,
          origin: "bulk",
        });
        toast.success(`Enfileiradas: ${data.queued} · Sem telefone: ${data.skipped_no_phone} · Bloqueadas: ${data.skipped_forbidden} · Erros: ${data.errors}`);
      } else {
        const c = clients[0];
        const payload = {
          client_id: c?.id,
          template_code: chosenCode || null,
          content: chosenCode ? null : content,
          category: category || null,
          origin: "manual",
        };
        if (contract?.id) {
          payload.contract_id = contract.id;
          payload.contract_type = contract.contract_type || (contract.is_installment ? "installment_contract" : "contract");
        }
        const { data } = await api.post("/whatsapp/send", payload);
        toast.success(`Mensagem registrada (status: ${data.status})`);
      }
      onSent?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar");
    } finally { setSending(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="send-whatsapp-modal">
      <div className="fc-card p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300">WhatsApp</div>
            <h3 className="font-display text-lg font-bold flex items-center gap-2 mt-0.5">
              {isBulk ? <><Users size={18} className="text-emerald-400"/> Envio em massa ({clients.length})</> : <><MessageSquare size={18} className="text-emerald-400"/> Enviar mensagem</>}
            </h3>
          </div>
          <button onClick={onClose}><X size={18}/></button>
        </div>

        {!config && (
          <div className="mb-4 p-3 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-200 text-[12px]" data-testid="no-config-warn">
            Nenhum número de WhatsApp configurado. Vá em <strong>Administração → WhatsApp</strong> para configurar.
          </div>
        )}
        {config && (
          <div className="text-[11px] text-[#A3A3A3] mb-3">
            Enviando por <span className="font-mono-tab text-emerald-300">+{config.phone_number}</span>
            {config.phone_label && ` (${config.phone_label})`} · <span className="text-[#737373]">{config.provider}</span>
          </div>
        )}

        {!isBulk && (
          <div className="mb-3 text-[12px] text-[#A3A3A3]">
            Para: <span className="text-[#E5E5E5] font-medium">{clients[0]?.full_name || "—"}</span>
            {clients[0]?.whatsapp && <span className="ml-2 font-mono-tab text-[11px] text-[#737373]">({clients[0].whatsapp})</span>}
            {contract?.code && <span className="ml-2 text-[10.5px] text-[#D4AF37]">· Contrato {contract.code}</span>}
          </div>
        )}

        <div className="mb-3">
          <div className="text-[11px] text-[#737373] uppercase mb-1.5">Template</div>
          <select className="fc-input text-[13px]" value={chosenCode} onChange={e=>applyTemplate(e.target.value)} data-testid="send-template-select">
            <option value="">— sem template (texto livre) —</option>
            {sortedTemplates.map(t => (
              <option key={t.code} value={t.code}>[{t.category}] {t.name}</option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <div className="text-[11px] text-[#737373] uppercase mb-1.5">Conteúdo</div>
          <textarea rows={5} className="fc-input text-[13px]" value={content} onChange={e=>{setContent(e.target.value); setPreview("");}} data-testid="send-content" placeholder="Digite a mensagem ou escolha um template..."/>
        </div>

        {preview && !isBulk && (
          <div className="mb-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-[12.5px] text-emerald-100 whitespace-pre-wrap" data-testid="send-preview">
            <div className="text-[10px] uppercase tracking-wider text-emerald-300 mb-1">Preview para {clients[0]?.full_name}</div>
            {preview}
          </div>
        )}

        {isBulk && (
          <div className="mb-3 text-[11px] text-[#A3A3A3] p-3 rounded-md bg-[#0a0a0a] border border-[#1f1f1f]">
            <div className="text-emerald-300">Envio em massa</div>
            Serão criadas <strong className="text-[#E5E5E5]">{clients.length}</strong> mensagens (uma por cliente).
            Clientes sem número de WhatsApp cadastrado serão automaticamente ignorados.
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="fc-btn-ghost text-xs">Cancelar</button>
          <button onClick={doSend} disabled={sending || !config || !content} className="fc-btn-primary text-xs flex items-center gap-2" data-testid="send-submit">
            {sending ? <Loader2 size={13} className="animate-spin"/> : <Send size={13}/>}
            {isBulk ? `Enviar (${clients.length})` : "Enviar"}
          </button>
        </div>
      </div>
    </div>
  );
}
